import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  cleanFilters,
  dashboardAnalyticsService,
  type AnalyticsFilters,
  type FlowDateField,
  type PeopleRole,
  type PivotRequest,
} from '@/services/dashboardAnalytics'

/** Same cadence as the boards: a briefing left open refreshes itself. */
const ANALYTICS_REFETCH_MS = 60_000
const ANALYTICS_STALE_MS = 30_000

/** Every analytics query shares this root so one invalidation refreshes all. */
export const ANALYTICS_KEY = ['dashboards', 'analytics'] as const

function stableFilters(filters: AnalyticsFilters | undefined): string {
  const clean = cleanFilters(filters)
  return JSON.stringify(
    Object.keys(clean)
      .sort()
      .map((key) => [key, clean[key]]),
  )
}

const common = {
  staleTime: ANALYTICS_STALE_MS,
  refetchInterval: ANALYTICS_REFETCH_MS,
  refetchOnWindowFocus: true,
  // Changing a filter keeps the previous answer on screen until the next one
  // lands — a board that blanks to skeletons on every click reads as broken.
  placeholderData: keepPreviousData,
  // A 4xx is a contract answer (unknown dimension, unknown person) — retrying
  // it only delays the message.
  retry: (count: number, error: unknown) => {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status && status >= 400 && status < 500) return false
    return count < 2
  },
}

export function useAnalyticsDimensions() {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'dimensions'],
    queryFn: ({ signal }) => dashboardAnalyticsService.dimensions(signal),
    staleTime: 10 * 60_000,
    retry: common.retry,
  })
}

export function usePivot(request: PivotRequest | null) {
  return useQuery({
    queryKey: [
      ...ANALYTICS_KEY,
      'pivot',
      request?.dimension,
      request?.by ?? null,
      stableFilters(request?.filters),
      request?.sort ?? null,
      request?.limit ?? null,
    ],
    queryFn: ({ signal }) => dashboardAnalyticsService.pivot(request as PivotRequest, signal),
    enabled: Boolean(request?.dimension),
    ...common,
  })
}

export function useFunnel(filters: AnalyticsFilters | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'funnel', stableFilters(filters)],
    queryFn: ({ signal }) => dashboardAnalyticsService.funnel(cleanFilters(filters), signal),
    enabled,
    ...common,
  })
}

export function useFlow(
  filters: AnalyticsFilters | undefined,
  dateField: FlowDateField,
  months = 24,
  enabled = true,
) {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'flow', stableFilters(filters), dateField, months],
    queryFn: ({ signal }) =>
      dashboardAnalyticsService.flow({ filters: cleanFilters(filters), dateField, months }, signal),
    enabled,
    ...common,
  })
}

export function usePeople(role: PeopleRole, enabled = true) {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'people', role],
    queryFn: ({ signal }) => dashboardAnalyticsService.people(role, signal),
    enabled,
    ...common,
  })
}

export function usePersonProfile(role: PeopleRole, name: string | null) {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'person', role, name],
    queryFn: ({ signal }) => dashboardAnalyticsService.person(role, name as string, signal),
    enabled: Boolean(name),
    ...common,
    // A different person is a different page: never show the last person's
    // numbers under the new person's name while the request is in flight.
    placeholderData: undefined,
  })
}

export function useDealFacts(filters: AnalyticsFilters | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'deals', stableFilters(filters)],
    queryFn: ({ signal }) => dashboardAnalyticsService.deals(cleanFilters(filters), signal),
    enabled,
    ...common,
  })
}
